import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/authSlice';
import { LevelCard } from '@/components/Level/LevelCard';
import { ProfileMenu } from '@/components/ProfileMenu/ProfileMenu';
import { apiClient } from '@/services/api';
// SSE not used for level selection - using polling for cross-device sync
import './LevelSelection.css';

// Level definitions based on requirements
const LEVELS = [
  {
    id: 'lunar',
    name: 'Lunar Outpost',
    description: 'Master the basics of resource management on the Moon. Learn to mine, farm, and trade.',
    // Using custom LunarLevelImage component (no imageUrl needed)
    isUnlocked: true,
  },
  {
    id: 'martian',
    name: 'Martian Colony',
    description: 'Expand to Mars with advanced challenges and new resources.',
    imageUrl: '/icons/crater-8bit.png', // Placeholder
    isUnlocked: false,
  },
  {
    id: 'asteroid',
    name: 'Asteroid Mining',
    description: 'Navigate the asteroid belt and extract rare minerals.',
    imageUrl: '/icons/minerals-8bit.png', // Placeholder
    isUnlocked: false,
  },
  {
    id: 'station',
    name: 'Space Station',
    description: 'Build and manage a thriving orbital station.',
    imageUrl: '/icons/base-8bit.png', // Placeholder
    isUnlocked: false,
  },
  {
    id: 'dyson',
    name: 'Dyson Sphere',
    description: 'Harness the power of a star with megastructure engineering.',
    imageUrl: '/icons/energy-8bit.png', // Placeholder
    isUnlocked: false,
  },
  {
    id: 'wormhole',
    name: 'Wormhole Gateway',
    description: 'Master interstellar trade and exploration.',
    imageUrl: '/icons/helium3-8bit.png', // Placeholder
    isUnlocked: false,
  },
];

export function LevelSelectionPage() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // POLLING FALLBACK: Check for active games every 5 seconds
  // This ensures cross-device sync works even if WebSocket isn't fully set up
  useEffect(() => {
    if (!user?.userId) return;

    console.log('🔍 Starting polling for active games for user:', user.userId);

    const pollForGames = async () => {
      try {
        const response = await apiClient.listLobbies();
        const lobbies = response.games || []; // Extract games array from response
        
        // Find games where this user is a player
        const myGames = lobbies.filter((game: any) =>
          game.players?.some((p: any) => p.userId === user.userId || p.username === user.username || p.username === user.email)
        );

        if (myGames.length > 0) {
          // Find game in avatar_selection phase
          const activeGame = myGames.find((g: any) => g.phase === 'avatar_selection' || g.status === 'lobby');
          
          if (activeGame) {
            console.log('🎮 Found active game from another device! Navigating:', activeGame.gameId);
            navigate(`/lobby/avatars/${activeGame.gameId}`);
          }
        }
      } catch (error) {
        console.error('Error polling for games:', error);
        // Don't show error to user - this is a background operation
      }
    };

    // Poll immediately once
    pollForGames();

    // Then poll every 5 seconds
    const interval = setInterval(pollForGames, 5000);

    return () => {
      console.log('🔍 Stopping polling for active games');
      clearInterval(interval);
    };
  }, [user?.userId, user?.username, user?.email, navigate]);

  // Cross-device sync via polling (SSE not available in LocalStack for level selection)
  // Polling already set up above - no additional listeners needed

  const handleLevelSelect = (levelId: string) => {
    navigate(`/lobby/setup/${levelId}`);
  };

  return (
    <div className="level-selection-page">
      <header className="level-header">
        <h1>R.U.S.H.</h1>
        <div className="user-info">
          <span>Welcome, {user?.name || user?.username || user?.email}</span>
          <ProfileMenu />
        </div>
      </header>

      <main className="level-main">
        <div className="level-container">
          <h2 className="page-title">Select Your Mission</h2>
          <p className="page-subtitle">
            Choose a level to begin your resource management journey
          </p>

          <div className="level-grid">
            {LEVELS.map((level) => (
              <LevelCard
                key={level.id}
                levelId={level.id}
                name={level.name}
                description={level.description}
                imageUrl={level.imageUrl}
                isUnlocked={level.isUnlocked}
                onSelect={handleLevelSelect}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

